import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatMonthYear } from '../../lib/format-utils';
import { useAuth } from '../../context/AuthContext';
import { getPendingTimesheets, getAdminExpenseReports, getSalaryReport, getAllVacationPlans, getReopenRequests, getOverdueStatus, getTimesheetStatus, getMonthInfo, getProjects, getMonthlyStatus, getExpenseMonthlyStatus, getFlexBalance } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Users, CheckSquare, FileSpreadsheet, Clock, FolderKanban, AlertCircle, Receipt, Palmtree, RotateCcw, Check, X, AlertTriangle, Calendar, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { reviewReopenRequest } from '../../lib/api';
import OnboardingChecklist from '../../components/admin/OnboardingChecklist';

export default function AdminDashboard() {
    const { t } = useTranslation();
    const { user, updateUser, isOwner } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        pendingTimesheets: 0,
        pendingExpenses: 0,
        pendingExpenseAmount: 0,
        pendingVacations: 0,
        totalHoursThisMonth: 0,
        totalMilesThisMonth: 0
    });
    const [reopenRequests, setReopenRequests] = useState([]);
    const [processingReopen, setProcessingReopen] = useState(null);
    const [budgetWarningProjects, setBudgetWarningProjects] = useState([]);
    const [flexLimits, setFlexLimits] = useState({ min: -40, max: 80 });

    // Personal stats for admin
    const [personalStats, setPersonalStats] = useState({
        overdue: null,
        timesheetStatus: null,
        monthInfo: null,
        totalHours: 0,
        expectedHours: 0
    });

    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    // Month selector for team status table
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [teamStatus, setTeamStatus] = useState([]);
    const [expenseStatus, setExpenseStatus] = useState({});
    const [teamStatusLoading, setTeamStatusLoading] = useState(false);
    const [selectedMonthExpectedHours, setSelectedMonthExpectedHours] = useState(160);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        fetchTeamStatus();
    }, [selectedYear, selectedMonth]);

    const fetchData = async () => {
        try {
            const [timesheetsRes, expensesRes, reportRes, vacationRes, reopenRes, overdueRes, timesheetStatusRes, monthInfoRes, projectsRes, flexRes] = await Promise.all([
                getPendingTimesheets(),
                getAdminExpenseReports('submitted').catch(() => ({ data: [] })),
                getSalaryReport(currentMonth, currentYear).catch(() => ({ data: { reports: [] } })),
                getAllVacationPlans('pending').catch(() => ({ data: [] })),
                getReopenRequests().catch(() => ({ data: [] })),
                getOverdueStatus().catch(() => ({ data: { overdue: false } })),
                getTimesheetStatus(currentMonth, currentYear).catch(() => ({ data: null })),
                getMonthInfo(currentMonth, currentYear).catch(() => ({ data: { expected_hours: 160, working_days: 20 } })),
                getProjects({ all: true }).catch(() => ({ data: [] })),
                getFlexBalance().catch(() => ({ data: { balance: 0, flex_limit_min: -40, flex_limit_max: 80 } }))
            ]);

            const reports = reportRes.data.reports || [];
            const totalHours = reports.reduce((sum, r) => sum + r.total_hours, 0);
            const totalMiles = reports.reduce((sum, r) => sum + (r.total_miles || 0), 0);

            // Use V2 expense reports - count pending reports and sum their grand_total
            const pendingExpenseReports = expensesRes.data || [];
            const pendingExpenseAmount = pendingExpenseReports.reduce((sum, r) => sum + (r.grand_total || 0), 0);

            // Find current user's hours from reports
            const myReport = reports.find(r => r.user_id === user?.id);
            const myHours = myReport?.total_hours || 0;

            setStats({
                pendingTimesheets: timesheetsRes.data.length,
                pendingExpenses: pendingExpenseReports.length,
                pendingExpenseAmount,
                pendingVacations: vacationRes.data?.length || 0,
                totalHoursThisMonth: totalHours,
                totalMilesThisMonth: totalMiles
            });

            setPersonalStats({
                overdue: overdueRes.data,
                timesheetStatus: timesheetStatusRes.data,
                monthInfo: monthInfoRes.data,
                totalHours: myHours,
                expectedHours: monthInfoRes.data?.expected_hours || 160
            });

            if (user && flexRes.data?.balance != null) {
                updateUser({ ...user, flex_balance: flexRes.data.balance });
            }
            if (flexRes?.data) {
                setFlexLimits({
                    min: flexRes.data.flex_limit_min ?? -40,
                    max: flexRes.data.flex_limit_max ?? 80,
                });
            }

            // Filter to only pending reopen requests
            setReopenRequests((reopenRes.data || []).filter(r => r.status === 'pending'));

            const projectsData = projectsRes.data || [];
            const nearBudget = projectsData.filter(p =>
                p.budget_sek && p.budget_percentage_reported != null && p.budget_percentage_reported >= 90
            );
            setBudgetWarningProjects(nearBudget);
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTeamStatus = async () => {
        setTeamStatusLoading(true);
        try {
            const [statusRes, monthInfoRes, expenseStatusRes] = await Promise.all([
                getMonthlyStatus(selectedYear, selectedMonth),
                getMonthInfo(selectedMonth, selectedYear).catch(() => ({ data: { expected_hours: 160 } })),
                getExpenseMonthlyStatus(selectedYear, selectedMonth).catch(() => ({ data: [] })),
            ]);
            setTeamStatus(statusRes.data || []);
            setSelectedMonthExpectedHours(monthInfoRes.data?.expected_hours || 160);
            // Index expense status by user_id for O(1) lookup in the table
            const expByUser = {};
            (expenseStatusRes.data || []).forEach(e => { expByUser[e.user_id] = e; });
            setExpenseStatus(expByUser);
        } catch (error) {
            console.error('Failed to fetch team monthly status:', error);
        } finally {
            setTeamStatusLoading(false);
        }
    };

    const handleReopenAction = async (requestId, action) => {
        setProcessingReopen(requestId);
        try {
            await reviewReopenRequest(requestId, action);
            toast.success(t('admin.dashboard.reopen_action_result', { action: action === 'approve' ? t('admin.dashboard.reopen_approved') : t('admin.dashboard.reopen_rejected') }));
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('admin.dashboard.failed_process'));
        } finally {
            setProcessingReopen(null);
        }
    };

    const handleGoToOverdueMonth = () => {
        if (personalStats.overdue?.overdue) {
            sessionStorage.setItem('targetMonth', personalStats.overdue.month);
            sessionStorage.setItem('targetYear', personalStats.overdue.year);
        }
        navigate('/dashboard/log-reports');
    };

    // Build month options for the selector: rolling 24 months up to current month
    const monthOptions = [];
    for (let i = 0; i < 24; i++) {
        let m = currentMonth - i;
        let y = currentYear;
        while (m <= 0) { m += 12; y -= 1; }
        monthOptions.push({ year: y, month: m, label: formatMonthYear(y, m) });
    }

    // Derived counts for the summary metric cards
    const teamSubmittedCount = teamStatus.filter(e => e.status === 'submitted' || e.status === 'approved').length;
    const teamPendingCount = teamStatus.filter(e => e.status === 'submitted').length;
    const teamApprovedCount = teamStatus.filter(e => e.status === 'approved').length;
    const teamNotSubmittedCount = teamStatus.filter(e => e.status === 'not_submitted').length;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="admin-dashboard">
            {/* Onboarding checklist — owners only, auto-hides when all items complete or dismissed */}
            {isOwner && <OnboardingChecklist />}

            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">
                        {t('admin.dashboard.title', { name: user?.name?.split(' ')[0] })}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {formatMonthYear(currentYear, currentMonth)} {t('admin.dashboard.overview_subtitle')}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        className="uppercase tracking-wider"
                        onClick={() => navigate('/dashboard/log-reports')}
                    >
                        <Calendar className="w-4 h-4 mr-2" />
                        {t('admin.dashboard.my_timesheet')}
                    </Button>
                </div>
            </div>

            {/* Personal Overdue Warning — kept as a top-of-page banner because it
                signals the admin's *own* past-due timesheet, not an approval queue. */}
            {personalStats.overdue?.overdue && (
                <Card className="border-2 border-destructive shadow-none bg-red-50" data-testid="personal-overdue-warning">
                    <CardContent className="p-4 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-4 min-w-0">
                            <div className="w-10 h-10 bg-destructive rounded flex items-center justify-center flex-shrink-0">
                                <AlertTriangle className="w-5 h-5 text-white" strokeWidth={1.5} />
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-heading font-bold text-destructive">
                                    {t('admin.dashboard.overdue_title')}
                                </h3>
                                <p className="text-sm text-red-600">
                                    {formatMonthYear(personalStats.overdue.year, personalStats.overdue.month)}
                                    {personalStats.overdue.entries_count > 0 && ` ${t('admin.dashboard.overdue_entries', { count: personalStats.overdue.entries_count })}`}
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="destructive"
                            size="sm"
                            className="uppercase tracking-wider flex-shrink-0"
                            onClick={handleGoToOverdueMonth}
                        >
                            {t('common.complete_now')}
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Budget Warning — projects at or above 90% of their budget */}
            {budgetWarningProjects.length > 0 && (
                <Card className="border border-amber-300 shadow-none bg-amber-50" data-testid="budget-warning-section">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <span className="text-sm font-medium text-amber-900">
                                {t('admin.dashboard.budget_warning', { count: budgetWarningProjects.length })}
                            </span>
                        </div>
                        <div className="space-y-2">
                            {budgetWarningProjects.map(project => (
                                <div key={project.id} className="flex items-center gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between text-xs mb-0.5">
                                            <span className="font-medium truncate">{project.name}</span>
                                            <span className={`font-mono ml-2 flex-shrink-0 ${project.budget_percentage_reported >= 100 ? 'text-destructive font-bold' : 'text-amber-700'}`}>
                                                {project.budget_percentage_reported?.toFixed(1)}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-amber-200 rounded-full h-1.5 overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${project.budget_percentage_reported >= 100 ? 'bg-destructive' : 'bg-amber-500'}`}
                                                style={{ width: `${Math.min(project.budget_percentage_reported, 100)}%` }}
                                            />
                                        </div>
                                        <div className="flex justify-between text-xs text-muted-foreground mt-0.5">
                                            <span>{project.budget_reported_sek?.toLocaleString()} SEK reported</span>
                                            <span>{project.budget_sek?.toLocaleString()} SEK budget</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Personal Stats Row */}
            <div>
                <h2 className="font-heading text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    {t('admin.dashboard.my_status')}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="border border-border shadow-none">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-muted-foreground uppercase tracking-widest">{t('admin.dashboard.my_hours')}</span>
                            </div>
                            <div className="text-2xl font-bold tabular-nums">
                                {personalStats.totalHours}
                                <span className="text-sm text-muted-foreground font-normal">
                                    /{personalStats.expectedHours}h
                                </span>
                            </div>
                            <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-foreground transition-all duration-500"
                                    style={{ width: `${Math.min((personalStats.totalHours / personalStats.expectedHours) * 100, 100)}%` }}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border border-border shadow-none">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-muted-foreground uppercase tracking-widest">{t('admin.dashboard.flex_balance')}</span>
                            </div>
                            <div className={`text-2xl font-bold tabular-nums ${user?.flex_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {user?.flex_balance > 0 ? '+' : ''}{user?.flex_balance || 0}h
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {t('admin.dashboard.flex_limit', { max: flexLimits.max ?? 80, min: flexLimits.min ?? -40 })}
                            </p>
                        </CardContent>
                    </Card>

                    <Card className="border border-border shadow-none">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-muted-foreground uppercase tracking-widest">{t('admin.dashboard.timesheet_status')}</span>
                            </div>
                            <div className="text-lg font-bold">
                                {personalStats.timesheetStatus?.status === 'approved' ? (
                                    <span className="text-green-600">{t('common.approved')}</span>
                                ) : personalStats.timesheetStatus?.status === 'submitted' ? (
                                    <span className="text-amber-600">{t('common.pending')}</span>
                                ) : (
                                    <span className="text-muted-foreground">{t('common.draft')}</span>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{formatMonthYear(currentYear, currentMonth)}</p>
                        </CardContent>
                    </Card>

                    <Card className="border border-border shadow-none">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-muted-foreground uppercase tracking-widest">{t('admin.dashboard.working_days')}</span>
                            </div>
                            <div className="text-2xl font-bold tabular-nums">
                                {personalStats.monthInfo?.working_days || 0}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{t('admin.dashboard.this_month')}</p>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Admin Section Header */}
            <div className="border-t pt-6">
                <h2 className="font-heading text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    {t('admin.dashboard.admin_overview')}
                </h2>
            </div>

            {/* ── Team Submission Status ── */}
            <div className="space-y-4" data-testid="team-submission-status">
                {/* Month selector + section title */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="font-heading text-base font-semibold tracking-tight">
                        {t('admin.dashboard.team_status')}
                    </h3>
                    <div className="flex items-center gap-2">
                        <label htmlFor="team-month-select" className="text-xs text-muted-foreground uppercase tracking-widest">
                            {t('admin.dashboard.select_month_label')}
                        </label>
                        <select
                            id="team-month-select"
                            className="border border-border rounded px-2 py-1 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                            value={`${selectedYear}-${String(selectedMonth).padStart(2, '0')}`}
                            onChange={(e) => {
                                const [y, m] = e.target.value.split('-').map(Number);
                                setSelectedYear(y);
                                setSelectedMonth(m);
                            }}
                        >
                            {monthOptions.map(o => (
                                <option
                                    key={`${o.year}-${o.month}`}
                                    value={`${o.year}-${String(o.month).padStart(2, '0')}`}
                                >
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Summary metric cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="border border-border bg-background px-4 py-3 text-center rounded">
                        <div className="text-2xl font-mono font-bold tabular-nums">{teamSubmittedCount}</div>
                        <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">{t('admin.dashboard.metric_submitted')}</div>
                    </div>
                    <div className="border border-border bg-background px-4 py-3 text-center rounded">
                        <div className="text-2xl font-mono font-bold tabular-nums text-amber-600">{teamPendingCount}</div>
                        <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">{t('admin.dashboard.metric_pending')}</div>
                    </div>
                    <div className="border border-border bg-background px-4 py-3 text-center rounded">
                        <div className="text-2xl font-mono font-bold tabular-nums text-green-600">{teamApprovedCount}</div>
                        <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">{t('admin.dashboard.metric_approved')}</div>
                    </div>
                    <div className="border border-border bg-background px-4 py-3 text-center rounded">
                        <div className="text-2xl font-mono font-bold tabular-nums text-muted-foreground">{teamNotSubmittedCount}</div>
                        <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">{t('admin.dashboard.metric_not_submitted')}</div>
                    </div>
                </div>

                {/* Pending approvals quick links */}
                {(stats.pendingTimesheets > 0 || stats.pendingExpenses > 0) && (
                    <div className="flex flex-wrap gap-2" data-testid="pending-approvals-links">
                        {stats.pendingTimesheets > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="uppercase tracking-wider"
                                onClick={() => navigate('/dashboard/approvals')}
                            >
                                <CheckSquare className="w-4 h-4 mr-2" />
                                {t('admin.dashboard.review_timesheets', { count: stats.pendingTimesheets })}
                            </Button>
                        )}
                        {stats.pendingExpenses > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="uppercase tracking-wider"
                                onClick={() => navigate('/dashboard/expense-approvals')}
                            >
                                <Receipt className="w-4 h-4 mr-2" />
                                {t('admin.dashboard.review_expenses', { count: stats.pendingExpenses })}
                            </Button>
                        )}
                    </div>
                )}

                {/* Team status table */}
                <div className="border border-border rounded overflow-hidden">
                    {teamStatusLoading ? (
                        <div className="flex items-center justify-center h-24">
                            <div className="w-6 h-6 border-2 border-foreground border-t-transparent animate-spin"></div>
                        </div>
                    ) : teamStatus.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">{t('admin.dashboard.no_team_data')}</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border bg-muted/40">
                                    <th className="text-left px-4 py-2 text-xs text-muted-foreground uppercase tracking-widest font-medium">
                                        {t('admin.dashboard.col_employee')}
                                    </th>
                                    <th className="text-left px-4 py-2 text-xs text-muted-foreground uppercase tracking-widest font-medium">
                                        {t('admin.dashboard.col_hours')}
                                    </th>
                                    <th className="text-left px-4 py-2 text-xs text-muted-foreground uppercase tracking-widest font-medium">
                                        {t('admin.dashboard.col_flex')}
                                    </th>
                                    <th className="text-left px-4 py-2 text-xs text-muted-foreground uppercase tracking-widest font-medium">
                                        {t('admin.dashboard.col_status')}
                                    </th>
                                    <th className="text-left px-4 py-2 text-xs text-muted-foreground uppercase tracking-widest font-medium">
                                        {t('admin.dashboard.col_expense_status')}
                                    </th>
                                    <th className="text-right px-4 py-2 text-xs text-muted-foreground uppercase tracking-widest font-medium">
                                        {t('admin.dashboard.col_actions')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {teamStatus.map((emp, idx) => {
                                    const expectedHours = selectedMonthExpectedHours;
                                    const hoursLogged = emp.hours_logged ?? null;
                                    const flexBalance = emp.flex_balance ?? null;
                                    const hoursBarPct = hoursLogged !== null && expectedHours > 0
                                        ? Math.min(Math.max((hoursLogged / expectedHours) * 100, 0), 100)
                                        : 0;
                                    const flexColorClass = flexBalance === null
                                        ? 'text-muted-foreground'
                                        : flexBalance > 0
                                            ? 'text-green-600'
                                            : flexBalance < 0
                                                ? 'text-destructive'
                                                : 'text-muted-foreground';
                                    return (
                                        <tr
                                            key={emp.user_id}
                                            className={`border-b border-border last:border-0 ${idx % 2 === 0 ? '' : 'bg-muted/20'}`}
                                            data-testid={`team-row-${emp.user_id}`}
                                        >
                                            <td className="px-4 py-3 font-medium">{emp.name}</td>
                                            <td className="px-4 py-3">
                                                <span className="block tabular-nums">
                                                    {hoursLogged !== null ? `${hoursLogged.toFixed(1)}h` : '—'}
                                                </span>
                                                <div className="w-16 h-1 bg-muted rounded-full mt-1">
                                                    <div
                                                        className="h-1 bg-foreground rounded-full"
                                                        style={{ width: `${hoursBarPct}%` }}
                                                    />
                                                </div>
                                            </td>
                                            <td className={`px-4 py-3 tabular-nums ${flexColorClass}`}>
                                                {flexBalance !== null
                                                    ? `${flexBalance >= 0 ? '+' : ''}${flexBalance.toFixed(1)}h`
                                                    : '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                {emp.status === 'approved' && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                        {t('admin.dashboard.badge_approved')}
                                                    </span>
                                                )}
                                                {emp.status === 'submitted' && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                                        {t('admin.dashboard.badge_submitted')}
                                                    </span>
                                                )}
                                                {emp.status === 'rejected' && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                        {t('admin.dashboard.badge_rejected')}
                                                    </span>
                                                )}
                                                {emp.status === 'not_submitted' && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                                                        {t('admin.dashboard.badge_not_submitted')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {(() => {
                                                    const expEmp = expenseStatus[emp.user_id];
                                                    const expSt = expEmp?.status || 'not_submitted';
                                                    if (expSt === 'approved') return (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                            {t('admin.dashboard.badge_approved')}
                                                        </span>
                                                    );
                                                    if (expSt === 'submitted') return (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                                            {t('admin.dashboard.badge_submitted')}
                                                        </span>
                                                    );
                                                    if (expSt === 'rejected') return (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                            {t('admin.dashboard.badge_rejected')}
                                                        </span>
                                                    );
                                                    return (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                                                            {t('admin.dashboard.badge_not_submitted')}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {(emp.status === 'submitted' || (expenseStatus[emp.user_id]?.status === 'submitted')) && (
                                                    <button
                                                        className="text-xs text-foreground underline underline-offset-2 hover:text-muted-foreground transition-colors"
                                                        onClick={() => navigate(emp.status === 'submitted' ? '/dashboard/approvals' : '/dashboard/expense-approvals')}
                                                    >
                                                        {t('admin.dashboard.col_actions')}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Stats Grid — neutral stats only. Pending counts also live here as
                read-only references; the attention signal for pending approvals is
                the count badge on the corresponding sidebar nav item. */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="border border-border shadow-none cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/dashboard/approvals')}>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <CheckSquare className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                            <span className="text-xs text-muted-foreground uppercase tracking-widest">
                                {t('admin.dashboard.timesheets_label')}
                            </span>
                        </div>
                        <div className="stat-value tabular-nums">
                            {stats.pendingTimesheets}
                        </div>
                        <p className="stat-label">{t('admin.dashboard.pending_approval')}</p>
                    </CardContent>
                </Card>

                <Card className="border border-border shadow-none cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/dashboard/expense-approvals')}>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <Receipt className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                            <span className="text-xs text-muted-foreground uppercase tracking-widest">
                                {t('admin.dashboard.expenses_label')}
                            </span>
                        </div>
                        <div className="stat-value tabular-nums">
                            {stats.pendingExpenses}
                        </div>
                        <p className="stat-label">
                            {t('admin.dashboard.expense_pending', { amount: stats.pendingExpenseAmount.toLocaleString() })}
                        </p>
                    </CardContent>
                </Card>

                <Card className="border border-border shadow-none cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/dashboard/vacation-calendar')}>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <Palmtree className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                            <span className="text-xs text-muted-foreground uppercase tracking-widest">
                                {t('admin.dashboard.vacations_label')}
                            </span>
                        </div>
                        <div className="stat-value tabular-nums">
                            {stats.pendingVacations}
                        </div>
                        <p className="stat-label">{t('admin.dashboard.pending_approval')}</p>
                    </CardContent>
                </Card>

                <Card className="border border-border shadow-none cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/dashboard/reports')}>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <Clock className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                            <span className="text-xs text-muted-foreground uppercase tracking-widest">
                                {t('admin.dashboard.this_month_label')}
                            </span>
                        </div>
                        <div className="stat-value tabular-nums">{stats.totalHoursThisMonth}h</div>
                        <p className="stat-label">
                            {stats.totalMilesThisMonth > 0 && t('admin.dashboard.km_traveled', { count: stats.totalMilesThisMonth })}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Reopen Requests Section */}
            {reopenRequests.length > 0 && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                            <RotateCcw className="w-5 h-5 text-blue-600" />
                            {t('admin.dashboard.reopen_requests_title', { count: reopenRequests.length })}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {reopenRequests.map(request => (
                                <div
                                    key={request.id}
                                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                                    data-testid={`reopen-request-${request.id}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                                            {request.user_name?.charAt(0) || '?'}
                                        </div>
                                        <div>
                                            <p className="font-medium">{request.user_name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {formatDate(request.created_at)} • {request.month}/{request.year}
                                            </p>
                                            {request.reason && (
                                                <p className="text-sm text-muted-foreground italic mt-1">
                                                    &ldquo;{request.reason}&rdquo;
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                            onClick={() => handleReopenAction(request.id, 'approve')}
                                            disabled={processingReopen === request.id}
                                            data-testid={`approve-reopen-${request.id}`}
                                        >
                                            <Check className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => handleReopenAction(request.id, 'reject')}
                                            disabled={processingReopen === request.id}
                                            data-testid={`reject-reopen-${request.id}`}
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
